/**
 * Choisit la source des findings affichés par la review d'un dossier CG.
 *
 * Trois sources coexistent, par ordre d'autorité décroissante :
 *  1. le SNAPSHOT figé par FormCGIA à la soumission — rejoué verbatim, c'est la
 *     garantie que la review montre exactement ce que l'agence a vu ;
 *  2. le verdict LLM persisté (analyse v1, à défaut audit batch Opus), qui fait
 *     autorité sur les dossiers antérieurs au snapshot ;
 *  3. le filet regex recalculé à l'ouverture de la review.
 *
 * ⚠ Un snapshot VIDE ne prouve pas que le dossier est propre. Il fige ce que le
 * moteur disponible à la soumission avait produit, et le moteur regex se tait
 * quand une pièce maîtresse manque : sans carte grise, les règles qui la lisent
 * retournent toutes `null`. Sur DW-841-DG (#12439, retour François du
 * 13/08/2026) le snapshot valait `{engine: "regex", findings: []}` alors que
 * l'audit Opus, rendu ensuite, bloquait sur `CG-ABSENT` — la review affichait
 * donc zéro alerte sur un dossier sans carte grise, et le dossier passait pour
 * conforme. Un snapshot sans aucun finding ne masque donc plus un verdict qui,
 * lui, a vu quelque chose.
 *
 * Un dossier réellement sans anomalie rend le même écran qu'avant : snapshot
 * vide ET verdict LLM sans finding donnent une liste vide.
 *
 * Extrait du `useMemo` de `CGReviewPage` pour être PUR et testable.
 */

import type { CrossCheckFinding } from '../FormCGIA/validation/crossCheckEngine';

export interface SelectPiecesFindingsParams {
  /** Findings du snapshot figé à la soumission (vide si aucun snapshot). */
  snapshotFindings: readonly CrossCheckFinding[];
  /** Verdict LLM faisant autorité (v1 en priorité, sinon audit Opus), `null` si aucun n'a rendu. */
  llmFindings: readonly CrossCheckFinding[] | null;
  /** Filet regex recalculé à l'ouverture de la review. */
  regexFindings: readonly CrossCheckFinding[];
}

export const selectPiecesFindings = ({
  snapshotFindings,
  llmFindings,
  regexFindings,
}: SelectPiecesFindingsParams): CrossCheckFinding[] => {
  if (snapshotFindings.length > 0) return [...snapshotFindings];
  if (!llmFindings) return [...regexFindings];

  // Le regex ne contredit jamais le verdict qui fait autorité : ce qu'il ajoute
  // est conservé en note de diagnostic, ce qu'il répète est écarté.
  const llmRuleIds = new Set(llmFindings.map((f) => f.ruleId));
  const regexAsDiagnostic = regexFindings
    .filter((f) => !llmRuleIds.has(f.ruleId))
    .map((f): CrossCheckFinding => ({ ...f, severity: 'info' }));

  return [...llmFindings, ...regexAsDiagnostic];
};
